import React, { useState, useRef } from 'react'
import classes from './index.module.css'
import uploadIcon from '../../icons/uploadIcon.svg'
import editIcon from '../../icons/edit-icon.svg'
import PropTypes from 'prop-types'
import errorToast from '../../Toasts/ErrorToast'
import { useQueryClient } from '@tanstack/react-query'
import { useAPI } from '../../../Contexts/APIContext'
import Loader from '../../GeneralComponents/Loader'

const ImageUploader = ({
  collectionId,
  isPublic,
  currentImage,
  onUploadComplete,
  iconOnly,
  title,
  subtitle,
  accept,
  buttonText,
  formatValidator,
  getUploadUrl,
}) => {
  const inputRef = useRef()
  const queryClient = useQueryClient()
  const { uploadCollectionImage } = useAPI()
  const [isUploaderOpen, setIsUploaderOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [file, setFile] = useState(null)

  const handleUpload = async () => {
    if (!file || file.length === 0) return
    const selectedFile = file[0]

    // Optional format validation (e.g. PNG-only for gradient)
    if (formatValidator && !formatValidator(selectedFile)) {
      return
    }

    try {
      const image = new Image()
      image.src = URL.createObjectURL(selectedFile)
      await image.decode()
      if (image.width < 720) {
        errorToast('The image must be at least 720px wide.')
        return
      }

      setIsLoading(true)
      const uploadLink = await getUploadUrl(collectionId, selectedFile)
      await uploadCollectionImage(
        [uploadLink.upload_url[0]],
        selectedFile,
        selectedFile.type
      )
      if (onUploadComplete) {
        onUploadComplete(URL.createObjectURL(selectedFile))
      }
      queryClient.invalidateQueries({
        queryKey: ['collection', collectionId, isPublic],
      })
    } catch (e) {
      errorToast(`Error: ${e.status ?? ''} ${e.message ?? ''}`)
    } finally {
      setFile(null)
      setIsLoading(false)
      setIsUploaderOpen(false)
    }
  }

  const handleDragOver = event => {
    event.preventDefault()
  }

  const handleDrop = event => {
    event.preventDefault()
    event.stopPropagation()
    setFile(event.dataTransfer.files)
  }

  const handleContainerClick = event => {
    event.stopPropagation()
  }

  function onOpen() {
    setIsUploaderOpen(true)
  }

  function onClose() {
    setIsUploaderOpen(false)
    setFile(null)
  }

  const uploaderContent = (showPreview) => (
    <div className={classes.uploaderBgContainer} onClick={onClose}>
      <div
        className={classes.uploaderContainer}
        onClick={handleContainerClick}
      >
        <div className={classes.uploaderHeader}>
          <span className={classes.uploaderTitle}>{title}</span>
          <span className={classes.uploaderSubtitle}>{subtitle}</span>
        </div>
        <div
          className={classes.uploaderWrapper}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => inputRef.current.click()}
          style={{ cursor: 'pointer' }}
        >
          {showPreview && (
            <>
              <img
                className={classes.uploadedImage}
                src={file ? URL.createObjectURL(file[0]) : currentImage}
                alt="Preview"
              />
              <div className={classes.uploadedImageOverlay} />
            </>
          )}
          <input
            accept={accept}
            type="file"
            onChange={event => setFile(event.target.files)}
            hidden
            ref={inputRef}
          />
          <div className={classes.uploaderImageContainer}>
            <img
              className={classes.uploaderImage}
              src={uploadIcon}
              alt="upload_icon"
            />
          </div>
          <div className={classes.uploadTextSection}>
            <button
              onClick={(e) => { e.stopPropagation(); inputRef.current.click() }}
              className={classes.uploaderButton}
            >
              Click to Upload
            </button>
            <span className={classes.uploaderText}> or Drag and Drop</span>
          </div>
        </div>
        {!isLoading && (
          <div className={classes.uploaderBtnGroup}>
            <button className={classes.uploaderCancel} onClick={onClose}>
              Cancel
            </button>
            <button
              className={classes.uploaderSave}
              disabled={!file}
              onClick={handleUpload}
            >
              {buttonText}
            </button>
          </div>
        )}
        {isLoading && <Loader isLoading={isLoading} />}
      </div>
    </div>
  )

  return (
    <>
      {iconOnly ? (
        <button
          className={classes.editIconButton}
          type="button"
          onClick={onOpen}
          title={`Edit ${title.toLowerCase()}`}
        >
          <img src={editIcon} alt="Edit" className={classes.editIcon} />
        </button>
      ) : (
        <button
          className={currentImage ? classes.buttonChange : classes.buttonUpload}
          type="button"
          onClick={onOpen}
        >
          {currentImage ? `Change ${title}` : `Add ${title}`}
        </button>
      )}

      {isUploaderOpen && !file && uploaderContent(!!currentImage)}
      {isUploaderOpen && file && uploaderContent(true)}
    </>
  )
}

ImageUploader.propTypes = {
  collectionId: PropTypes.string.isRequired,
  isPublic: PropTypes.bool,
  currentImage: PropTypes.string,
  onUploadComplete: PropTypes.func,
  iconOnly: PropTypes.bool,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  accept: PropTypes.string.isRequired,
  buttonText: PropTypes.string.isRequired,
  formatValidator: PropTypes.func,
  getUploadUrl: PropTypes.func.isRequired,
}

export default ImageUploader
